// NEW_FILE_JUSTIFIED: New hook for driver form state management — no existing equivalent, usePolicyForm handles flat field values only
// HOOK_STATE_JUSTIFIED: driver array form state — extracted per max-2-useState rule
import { useState, useCallback } from 'react';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need two levels up to reach convex/_generated/
import type { IDriverFormData, IPolicyDriver } from '@/@types/insurance';

const EMPTY_DRIVER: IDriverFormData = {
  fullName: '',
  birthday: '',
  nationality: '',
  licenseIssueCountry: '',
  licenseIssueDate: '',
};

export function useDriverForm() {
  const [drivers, setDrivers] = useState<IDriverFormData[]>([]);
  const [mainDriverIndex, setMainDriverIndex] = useState<number | null>(null);

  const addDriver = useCallback(() => {
    setDrivers(prev => {
      const next = [...prev, { ...EMPTY_DRIVER }];
      return next;
    });
    setMainDriverIndex(prev => prev ?? 0);
  }, []);

  const removeDriver = useCallback((index: number) => {
    setDrivers(prev => prev.filter((_, i) => i !== index));
    setMainDriverIndex(prev => {
      if (prev === null) return null;
      if (prev === index) return null;
      if (index < prev) return prev - 1;
      return prev;
    });
  }, []);

  const updateDriver = useCallback((index: number, field: keyof IDriverFormData, value: string) => {
    setDrivers(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const setDriverFile = useCallback(
    (
      index: number,
      field: 'licenseFrontStorageId' | 'licenseBackStorageId',
      storageId: Id<'_storage'>
    ) => {
      setDrivers(prev => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: storageId };
        return next;
      });
    },
    []
  );

  const removeDriverFile = useCallback(
    (index: number, field: 'licenseFrontStorageId' | 'licenseBackStorageId') => {
      setDrivers(prev => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: undefined };
        return next;
      });
    },
    []
  );

  const initFromExisting = useCallback(
    (existingDrivers: IPolicyDriver[], mainDriverId?: Id<'policyDrivers'>) => {
      setDrivers(
        existingDrivers.map(d => ({
          fullName: d.fullName,
          birthday: d.birthday,
          nationality: d.nationality,
          idNumber: d.idNumber,
          licenseIssueCountry: d.licenseIssueCountry,
          licenseIssueDate: d.licenseIssueDate,
          licenseFrontStorageId: d.licenseFrontStorageId,
          licenseBackStorageId: d.licenseBackStorageId,
        }))
      );
      if (mainDriverId) {
        const idx = existingDrivers.findIndex(d => d._id === mainDriverId);
        setMainDriverIndex(idx >= 0 ? idx : null);
      } else {
        setMainDriverIndex(existingDrivers.length > 0 ? 0 : null);
      }
    },
    []
  );

  const toDriversPayload = useCallback(() => {
    return drivers.filter(d => d.fullName.trim() !== '');
  }, [drivers]);

  return {
    drivers,
    mainDriverIndex,
    setMainDriverIndex,
    addDriver,
    removeDriver,
    updateDriver,
    setDriverFile,
    removeDriverFile,
    initFromExisting,
    toDriversPayload,
  };
}
